import { useEffect, useRef, useState } from 'react'

function debounce<T extends (...args: any[]) => void>(fn: T, delay: number): T {
  let timeoutId: ReturnType<typeof setTimeout>
  return function (this: any, ...args: any[]) {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => fn.apply(this, args), delay)
  } as T
}

export default function useWindowSize(debounceMs = 200) {
  const [size, setSize] = useState({ width: 0, height: 0 })
  const [isChanged, setIsChanged] = useState(false)
  const prevSize = useRef({ width: 0, height: 0 })

  useEffect(() => {
    function handleResize() {
      const newSize = { width: window.innerWidth, height: window.innerHeight }
      setSize(newSize)

      const changed =
        newSize.width !== prevSize.current.width ||
        newSize.height !== prevSize.current.height

      if (changed) {
        setIsChanged(true)
        prevSize.current = newSize
        setTimeout(() => setIsChanged(false), debounceMs)
      }
    }

    const debouncedResize = debounce(handleResize, debounceMs)
    window.addEventListener('resize', debouncedResize)

    handleResize()

    return () => window.removeEventListener('resize', debouncedResize)
  }, [debounceMs])

  return { ...size, isChanged }
}
